import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, Send, Mic, Square, Volume2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const MODELS = [
  { id: "openai", label: "GPT-5.4" },
  { id: "claude", label: "Claude Sonnet 4.6" },
  { id: "gemini", label: "Gemini 3.1 Pro" },
];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [provider, setProvider] = useState("openai");
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([
    { role: "assistant", content: "I'm the GuardGig28 assistant. Ask me about the rules, pay, verification, or the no-show ban." },
  ]);
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [speaking, setSpeaking] = useState(-1);
  const sessionRef = useRef(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!sessionRef.current) {
      sessionRef.current = localStorage.getItem("gg28_chat_session") || crypto.randomUUID();
      localStorage.setItem("gg28_chat_session", sessionRef.current);
    }
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: msg, provider, session_id: sessionRef.current }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (payload === "[DONE]") break;
          try {
            const parsed = JSON.parse(payload);
            if (parsed.error) {
              setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: parsed.error }; return c; });
            } else if (parsed.delta) {
              setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: c[c.length - 1].content + parsed.delta }; return c; });
            }
          } catch { /* partial chunk */ }
        }
      }
    } catch {
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: "Connection failed — try again." }; return c; });
    } finally {
      setBusy(false);
    }
  };

  const toggleRecording = async () => {
    if (recording) {
      mediaRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => chunksRef.current.push(e.data);
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const form = new FormData();
        form.append("file", blob, "voice.webm");
        try {
          const res = await fetch(`${API}/chat/stt`, { method: "POST", body: form, credentials: "include" });
          const data = await res.json();
          if (data.text) setInput((prev) => (prev ? prev + " " : "") + data.text);
          else toast.error("Couldn't hear anything — try again.");
        } catch {
          toast.error("Transcription failed.");
        }
      };
      recorder.start();
      mediaRef.current = recorder;
      setRecording(true);
    } catch {
      toast.error("Microphone access denied.");
    }
  };

  const speak = async (text, idx) => {
    if (speaking >= 0) return;
    setSpeaking(idx);
    try {
      const res = await fetch(`${API}/chat/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      });
      const data = await res.json();
      if (data.url) {
        const audio = new Audio(`${BACKEND}${data.url}`);
        audio.onended = () => setSpeaking(-1);
        audio.onerror = () => setSpeaking(-1);
        await audio.play();
      } else setSpeaking(-1);
    } catch {
      setSpeaking(-1);
      toast.error("Speech playback failed.");
    }
  };

  return (
    <>
      <motion.button
        data-testid="chat-open-btn"
        onClick={() => setOpen(true)}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 2, type: "spring", stiffness: 200 }}
        className={`fixed bottom-6 left-6 z-[90] flex h-14 w-14 items-center justify-center rounded-full bg-amber-500 text-[#07090e] shadow-[0_0_30px_rgba(245,158,11,0.4)] transition-transform hover:scale-105 ${open ? "hidden" : ""}`}
      >
        <MessageSquare className="h-6 w-6" strokeWidth={2.25} />
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-6 left-6 z-[90] flex h-[560px] w-[92vw] max-w-[400px] flex-col overflow-hidden rounded-2xl border border-amber-500/25 bg-[#0b0f16] shadow-2xl"
          >
            <div className="flex items-center justify-between border-b border-white/8 px-5 py-4">
              <div>
                <p className="font-display text-base font-bold uppercase text-slate-50">Rules Assistant</p>
                <p className="font-mono-tag text-[8px] text-emerald-400">AI · answers instantly</p>
              </div>
              <button data-testid="chat-close-btn" onClick={() => setOpen(false)} className="text-slate-500 hover:text-slate-50">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex gap-1.5 border-b border-white/8 px-4 py-2.5">
              {MODELS.map((m) => (
                <button
                  key={m.id}
                  data-testid={`chat-model-${m.id}`}
                  onClick={() => setProvider(m.id)}
                  className={`rounded px-2.5 py-1.5 font-mono-tag text-[8px] transition-all ${
                    provider === m.id ? "bg-amber-500/15 text-amber-500 ring-1 ring-amber-500/50" : "text-slate-500 hover:text-slate-300"
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>

            <div ref={scrollRef} data-testid="chat-messages" className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`group relative max-w-[85%] rounded-xl px-3.5 py-2.5 text-sm leading-relaxed ${
                    m.role === "user" ? "bg-amber-500 text-[#07090e] font-medium" : "border border-white/8 bg-[#111520] text-slate-300"
                  }`}>
                    {m.content || (busy && i === messages.length - 1 ? <Loader2 className="h-4 w-4 animate-spin text-amber-500" /> : "")}
                    {m.role === "assistant" && m.content && (
                      <button
                        data-testid={`chat-speak-${i}`}
                        onClick={() => speak(m.content, i)}
                        className="absolute -right-8 top-1 text-slate-600 opacity-0 transition-opacity hover:text-amber-500 group-hover:opacity-100"
                        title="Read aloud"
                      >
                        {speaking === i ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Volume2 className="h-3.5 w-3.5" />}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="flex items-center gap-2 border-t border-white/8 p-3"
            >
              <button
                type="button"
                data-testid="chat-mic-btn"
                onClick={toggleRecording}
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md transition-all ${
                  recording ? "bg-red-500 text-white animate-pulse" : "border border-white/10 text-slate-400 hover:text-amber-500"
                }`}
              >
                {recording ? <Square className="h-4 w-4" fill="currentColor" /> : <Mic className="h-4 w-4" />}
              </button>
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about the rules…"
                className="h-10 flex-1 rounded-md border border-white/10 bg-[#07090e] px-3 text-sm text-slate-50 placeholder:text-slate-600 focus:border-amber-500/60 focus:outline-none"
              />
              <button
                type="submit"
                data-testid="chat-send-btn"
                disabled={busy || !input.trim()}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-amber-500 text-[#07090e] transition-all hover:bg-amber-400 disabled:opacity-40"
              >
                <Send className="h-4 w-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
